import { RootPathQueryQuery } from '@sceneio/graphql-queries/dist/generated/graphqlTypes'
import { useCallback } from 'react'
import { useInternalGraphql } from './useInternalGraphql'
import { useContentCreateVariant } from './content/useContentCreateVariant'
import { useRouter } from './useRouter'
import { useValidateContentPathIds } from './content/useValidateContentPathIds'

export const useRedirectToHomepage = () => {
  const { sendRequest: fetchRootPath } = useInternalGraphql({
    query: 'RootPathQueryDocument',
    runOnInit: false,
  })

  const createContentVariant = useContentCreateVariant()
  const validateContentPathIds = useValidateContentPathIds()
  const router = useRouter()

  const resolveEditorPath = async ({
    contentPath,
  }: {
    contentPath: RootPathQueryQuery['paths'][0]
  }) => {
    const { content, id: pathId } = contentPath || {}
    const { id: contentId, publishState } = content || {}

    let editorContentId = ''
    let editorPathId = ''

    if (publishState !== 'DRAFT') {
      await createContentVariant(
        contentId,
        {
          onSuccess: (createContentVariantData) => {
            if (!createContentVariantData) return
            const { createContentVariant } = createContentVariantData
            const { id: variantContentId, paths } = createContentVariant
            const contentVariantPath = paths[0]
            const { id: variantPathId } = contentVariantPath || {}
            editorContentId = variantContentId
            editorPathId = variantPathId
          },
        },
        { showNotification: false },
      )
    } else {
      editorContentId = contentId
      editorPathId = pathId
    }

    return { editorContentId, editorPathId }
  }

  return useCallback(
    async ({
      projectId,
      onSuccess,
      onError,
      openSiteSettings = false,
      themeId,
    }: {
      projectId: string
      onSuccess?: () => void
      onError?: () => void
      openSiteSettings?: boolean
      themeId?: string | null | (string | null)[]
    }) => {
      try {
        const { data, errors: gqlErrors } = await fetchRootPath({
          authContext: { projectId },
        })

        if (gqlErrors && gqlErrors.length > 0) {
          throw new Error(gqlErrors[0].message)
        }

        const { paths = [] } = data || {}

        const draftContentPath = paths.find(({ content }) => content.parentId)
        const contentPath = draftContentPath || paths[0]
        resolveEditorPath({ contentPath }).then(
          async ({ editorContentId, editorPathId }) => {
            if (editorContentId && editorPathId) {
              const { isValid } = await validateContentPathIds({
                contentId: editorContentId,
                pathId: editorPathId,
                projectId,
              })

              if (isValid) {
                router.pushRoute(
                  'liveEditorContent',
                  {
                    projectId,
                    contentId: editorContentId,
                    pathId: editorPathId,
                  },
                  {
                    ...(openSiteSettings && { openSiteSettings }),
                    ...(themeId && { themeId }),
                  },
                )
              }
            }
            if (onSuccess) {
              onSuccess()
            }
          },
        )
      } catch (error) {
        console.error(error)
        if (onError) onError()
      }
    },
    [fetchRootPath],
  )
}
